import {
  ArrowUp,
  Check,
  CircleCheck,
  ExternalLink,
  History,
  ListChecks,
  LoaderCircle,
  Plus,
  Sparkles,
  Trash2,
  TriangleAlert,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { renderAiText } from "@/lib/aiText";
import {
  askHubAssist,
  confirmHubAssist,
  deleteHubAssistConversation,
  fetchHubAssistStatus,
  HUB_AI_OPEN_EVENT,
  listHubAssistConversations,
  loadHubAssistConversation,
  newConversationId,
  saveHubAssistConversation,
  type HubAiOpenDetail,
  type HubAssistConversationSummary,
  type HubAssistMessage,
  type HubAssistProposal,
  type HubAssistStatus,
  type HubAssistStoredMessage,
} from "@/lib/hubAssist";
import { resolveHubAiContext } from "@/lib/hubAiContext";
import {
  hubAiErrorCode,
  hubAiErrorMessage,
  hubAiToolLabel,
  sendHubAiDraft,
  type HubAiProposedSend,
  type HubAiToolCall,
} from "@/lib/hubAi";
import { announceTasksChanged, formatDue } from "@/lib/tasks";
import { HUB_VERSION } from "@/lib/version";
import { cn } from "@/lib/utils";

type ProposalPhase = "ready" | "saving" | "done" | "cancelled" | "error";
type ProposalState = { phase: ProposalPhase; message?: string; include?: number[] };
type DraftPhase = "ready" | "sending" | "sent" | "cancelled" | "error";
type DraftState = { phase: DraftPhase; text: string; error?: string; permalink?: string };

type Turn = {
  id: string;
  role: "user" | "assistant" | "error";
  content: string;
  at: string;
  toolCalls?: HubAiToolCall[];
  elapsedMs?: number;
  proposals?: HubAssistProposal[];
  proposalState?: Record<string, ProposalState>;
  drafts?: HubAiProposedSend[];
  draftState?: Record<string, DraftState>;
};

type Thread = { id: string; turns: Turn[]; module: string };

/** One conversation shared by the floating panel and the /ai page while Hub stays open. */
let threadMemory: Thread = { id: newConversationId(), turns: [], module: "hub" };

const GEOMETRY_KEY = "atw-hub-ai-panel-v1";
const DEFAULT_W = 380;
const DEFAULT_H = 580;
const MIN_W = 320;
const MIN_H = 400;
const EDGE = 12;
const DESKTOP_QUERY = "(min-width: 981px)";

type Geometry = { x: number; y: number; w: number; h: number };

function clampGeometry(g: Geometry): Geometry {
  const bottomChrome = 44;
  const maxW = Math.max(MIN_W, window.innerWidth - EDGE * 2);
  const maxH = Math.max(MIN_H, window.innerHeight - bottomChrome - EDGE * 2);
  const w = Math.min(Math.max(g.w, MIN_W), maxW);
  const h = Math.min(Math.max(g.h, MIN_H), maxH);
  const x = Math.min(Math.max(g.x, EDGE), window.innerWidth - w - EDGE);
  const y = Math.min(Math.max(g.y, EDGE), window.innerHeight - bottomChrome - h - EDGE);
  return { x, y, w, h };
}

function defaultGeometry(): Geometry {
  return clampGeometry({
    w: DEFAULT_W,
    h: DEFAULT_H,
    x: window.innerWidth - DEFAULT_W - 16,
    y: window.innerHeight - 44 - DEFAULT_H - 12,
  });
}

function readGeometry(): Geometry {
  try {
    const raw = JSON.parse(localStorage.getItem(GEOMETRY_KEY) || "null") as Partial<Geometry> | null;
    if (raw && [raw.x, raw.y, raw.w, raw.h].every((n) => typeof n === "number" && Number.isFinite(n))) {
      return clampGeometry(raw as Geometry);
    }
  } catch {
    /* private mode / bad JSON */
  }
  return defaultGeometry();
}

function writeGeometry(g: Geometry) {
  try {
    localStorage.setItem(GEOMETRY_KEY, JSON.stringify(g));
  } catch {
    /* ignore */
  }
}

function useIsDesktop(): boolean {
  const [desktop, setDesktop] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setDesktop(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return desktop;
}

function turnId(): string {
  return Math.random().toString(36).slice(2, 10);
}

/** Model-facing history: answers plus what happened to their cards, so follow-ups make sense. */
function historyFor(turns: Turn[]): HubAssistMessage[] {
  const out: HubAssistMessage[] = [];
  for (const turn of turns) {
    if (turn.role === "error") continue;
    if (turn.role === "user") {
      out.push({ role: "user", content: turn.content });
      continue;
    }
    const notes: string[] = [];
    for (const p of turn.proposals || []) {
      const phase = turn.proposalState?.[p.id]?.phase ?? "ready";
      const outcome = phase === "done" ? "confirmed by the user" : phase === "cancelled" ? "cancelled by the user" : "not confirmed yet";
      notes.push(`[Suggested: ${p.preview} — ${outcome}]`);
    }
    for (const d of turn.drafts || []) {
      const phase = turn.draftState?.[d.id]?.phase ?? "ready";
      const outcome = phase === "sent" ? "sent" : phase === "cancelled" ? "cancelled" : "not sent";
      notes.push(`[Slack draft to ${d.channelName || d.channelId}: ${outcome}]`);
    }
    out.push({ role: "assistant", content: [turn.content, ...notes].join("\n\n").slice(0, 6000) });
  }
  return out.slice(-20);
}

function storedFrom(turns: Turn[]): HubAssistStoredMessage[] {
  return turns
    .filter((t) => t.role !== "error")
    .map((t) => ({
      role: t.role as "user" | "assistant",
      content: t.content,
      at: t.at,
      ...(t.role === "assistant"
        ? {
            meta: {
              toolCalls: t.toolCalls,
              elapsedMs: t.elapsedMs,
              proposals: t.proposals,
              proposalState: t.proposalState,
              drafts: t.drafts,
              draftState: t.draftState,
            },
          }
        : {}),
    }));
}

function turnsFrom(messages: HubAssistStoredMessage[]): Turn[] {
  return messages.map((m) => {
    const meta = (m.meta || {}) as Partial<Turn>;
    return {
      id: turnId(),
      role: m.role,
      content: m.content,
      at: m.at,
      toolCalls: meta.toolCalls,
      elapsedMs: meta.elapsedMs,
      proposals: meta.proposals,
      proposalState: meta.proposalState,
      drafts: meta.drafts,
      draftState: meta.draftState,
    };
  });
}

function timeLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const TOOL_LABELS: Record<string, string> = {
  get_today: "Today",
  list_meetings: "Meetings",
  list_calendar_events: "Calendar",
  list_tasks: "Tasks",
  list_recent_mail: "Mail",
  read_mail_thread: "Mail thread",
  propose_create_tasks: "Suggest tasks",
  propose_update_task: "Suggest change",
  propose_delete_task: "Suggest delete",
  propose_slack_message: "Slack draft",
};

/** Hub's own tools; anything else Hub AI calls comes from Slack's MCP server. */
const HUB_TOOL_NAMES = new Set(Object.keys(TOOL_LABELS).filter((name) => name !== "propose_slack_message"));

/** Hub doesn't store Slack content, so chats where Hub AI read Slack aren't saved to History. */
function usedSlack(turns: Turn[]): boolean {
  return turns.some((t) => Boolean(t.drafts?.length) || Boolean(t.toolCalls?.some((c) => !HUB_TOOL_NAMES.has(c.name))));
}

/** Conversations already removed from History because they started using Slack. */
const unsavedSlackChats = new Set<string>();

function ToolTrail({ calls, elapsedMs }: { calls: HubAiToolCall[]; elapsedMs?: number }) {
  const rows = new Map<string, { label: string; ok: boolean; count: number }>();
  for (const call of calls) {
    const label = TOOL_LABELS[call.name] || `Slack: ${hubAiToolLabel(call.name)}`;
    const key = `${label}:${call.ok}`;
    const row = rows.get(key);
    if (row) row.count += 1;
    else rows.set(key, { label, ok: call.ok, count: 1 });
  }
  return (
    <p className="hub-ai-panel-trail">
      Checked{" "}
      {[...rows.values()].map((row, i) => (
        <span key={`${row.label}-${row.ok}`} className={cn("hub-ai-panel-trail-item", !row.ok && "failed")}>
          {i ? " · " : ""}
          {row.label}
          {row.count > 1 ? ` ×${row.count}` : ""}
          {row.ok ? "" : " (failed)"}
        </span>
      ))}
      {elapsedMs ? <span className="hub-ai-panel-trail-time"> · {(elapsedMs / 1000).toFixed(1)}s</span> : null}
    </p>
  );
}

function priorityLabel(priority?: string): string {
  return priority === "high" ? "High" : priority === "low" ? "Low" : "";
}

type ProposalCardProps = {
  proposal: HubAssistProposal;
  state: ProposalState;
  onInclude: (include: number[]) => void;
  onConfirm: () => void;
  onCancel: () => void;
  onOpenTasks: () => void;
};

function ProposalCard({ proposal, state, onInclude, onConfirm, onCancel, onOpenTasks }: ProposalCardProps) {
  const saving = state.phase === "saving";
  const open = state.phase === "ready" || state.phase === "error" || saving;
  const include = state.include;
  let title = "Confirm before saving";
  let action = "Confirm";
  if (proposal.kind === "create_tasks") {
    const count = include ? include.length : proposal.tasks.length;
    title = proposal.source?.kind === "meeting" ? "Follow-up tasks" : "New tasks";
    action = count === 1 ? "Add 1 task" : `Add ${count} tasks`;
  } else if (proposal.kind === "delete_task") {
    title = "Delete task";
    action = "Delete";
  } else {
    title = "Change task";
    action = proposal.changes.status === "done" ? "Mark done" : "Save change";
  }
  return (
    <section className={cn("hub-ai-confirm", `is-${state.phase}`)} aria-label={title}>
      <p className="hub-ai-confirm-kicker">
        <ListChecks size={13} aria-hidden /> {title}
        {proposal.kind === "create_tasks" && proposal.source?.title ? (
          <span className="hub-ai-confirm-source"> · from {proposal.source.title}</span>
        ) : null}
      </p>
      {proposal.kind === "create_tasks" ? (
        <ul className="hub-ai-confirm-tasks">
          {proposal.tasks.map((task, index) => {
            const checked = include ? include.includes(index) : true;
            const meta = [formatDue(task.due ?? null), priorityLabel(task.priority)].filter(Boolean).join(" · ");
            return (
              <li key={`${proposal.id}-${index}`}>
                <label>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!open || saving}
                    onChange={() => {
                      const current = include ?? proposal.tasks.map((_, i) => i);
                      onInclude(checked ? current.filter((i) => i !== index) : [...current, index].sort((a, b) => a - b));
                    }}
                  />
                  <span>
                    <span className="hub-ai-confirm-task-title">{task.title}</span>
                    {meta ? <span className="hub-ai-confirm-task-meta">{meta}</span> : null}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="hub-ai-confirm-preview">{proposal.preview}</p>
      )}
      {state.phase === "error" && state.message ? (
        <p className="hub-ai-confirm-error" role="alert">
          {state.message}
        </p>
      ) : null}
      {state.phase === "done" ? (
        <p className="hub-ai-confirm-done">
          <CircleCheck size={14} aria-hidden /> {state.message || "Saved."}
          <button type="button" className="hub-ai-link-btn" onClick={onOpenTasks}>
            Open Tasks
          </button>
        </p>
      ) : null}
      {state.phase === "cancelled" ? <p className="hub-ai-confirm-done muted">Cancelled. Nothing changed.</p> : null}
      {open ? (
        <div className="hub-ai-confirm-actions">
          <button
            type="button"
            className={cn("hub-ai-confirm-btn", proposal.kind === "delete_task" && "danger")}
            disabled={saving || (proposal.kind === "create_tasks" && include?.length === 0)}
            onClick={onConfirm}
          >
            {saving ? <LoaderCircle size={13} className="spinning" aria-hidden /> : <Check size={13} aria-hidden />}
            {action}
          </button>
          <button type="button" className="hub-ai-cancel-btn" disabled={saving} onClick={onCancel}>
            Cancel
          </button>
        </div>
      ) : null}
    </section>
  );
}

type DraftCardProps = {
  draft: HubAiProposedSend;
  state: DraftState;
  onText: (text: string) => void;
  onSend: () => void;
  onCancel: () => void;
};

function SlackDraftCard({ draft, state, onText, onSend, onCancel }: DraftCardProps) {
  const sending = state.phase === "sending";
  const open = state.phase === "ready" || state.phase === "error" || sending;
  return (
    <section className={cn("hub-ai-confirm", `is-${state.phase}`)} aria-label="Slack draft">
      <p className="hub-ai-confirm-kicker">
        Slack draft · {draft.channelName || draft.channelId}
        {draft.threadTs ? " (thread)" : ""}
      </p>
      {open ? (
        <textarea
          className="hub-ai-confirm-edit"
          aria-label="Edit draft"
          value={state.text}
          rows={Math.min(8, Math.max(3, state.text.split("\n").length + 1))}
          maxLength={4000}
          disabled={sending}
          onChange={(event) => onText(event.target.value)}
        />
      ) : (
        <div className="hub-ai-confirm-preview">{renderAiText(state.text)}</div>
      )}
      {state.phase === "error" && state.error ? (
        <p className="hub-ai-confirm-error" role="alert">
          {state.error}
        </p>
      ) : null}
      {state.phase === "sent" ? (
        <p className="hub-ai-confirm-done">
          <CircleCheck size={14} aria-hidden /> Sent to Slack.
          {state.permalink ? (
            <a className="chat-link" href={state.permalink} target="_blank" rel="noopener noreferrer">
              Open <ExternalLink size={11} aria-hidden />
            </a>
          ) : null}
        </p>
      ) : null}
      {state.phase === "cancelled" ? <p className="hub-ai-confirm-done muted">Cancelled. Nothing was sent.</p> : null}
      {open ? (
        <div className="hub-ai-confirm-actions">
          <button type="button" className="hub-ai-confirm-btn" disabled={sending || !state.text.trim()} onClick={onSend}>
            {sending ? <LoaderCircle size={13} className="spinning" aria-hidden /> : <Check size={13} aria-hidden />}
            Send
          </button>
          <button type="button" className="hub-ai-cancel-btn" disabled={sending} onClick={onCancel}>
            Cancel
          </button>
        </div>
      ) : null}
    </section>
  );
}

export type HubAiPanelProps = {
  /** floating: draggable window opened from the bottom bar. page: fills the /ai route. */
  mode: "floating" | "page";
};

export function HubAiPanel({ mode }: HubAiPanelProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const desktop = useIsDesktop();
  const pageCtx = resolveHubAiContext(pathname);
  const [open, setOpen] = useState(mode === "page");
  const [thread, setThreadState] = useState<Thread>(() => threadMemory);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [status, setStatus] = useState<HubAssistStatus | null>(null);
  const [statusError, setStatusError] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<HubAssistConversationSummary[] | null>(null);
  const [historyError, setHistoryError] = useState("");
  const [geometry, setGeometry] = useState<Geometry>(() => readGeometry());
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const focusRef = useRef<Record<string, unknown> | undefined>(undefined);
  const threadRef = useRef(thread);
  const floating = mode === "floating" && desktop;

  const setThread = useCallback((update: Thread | ((prev: Thread) => Thread)) => {
    if (typeof update !== "function") {
      // Plain values land in the ref now, so a send right after (e.g. a fresh chat) sees them.
      threadMemory = update;
      threadRef.current = update;
      setThreadState(update);
      return;
    }
    setThreadState((prev) => {
      const next = update(prev);
      threadMemory = next;
      threadRef.current = next;
      return next;
    });
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await fetchHubAssistStatus());
      setStatusError("");
    } catch (err) {
      setStatusError(hubAiErrorMessage(err, "Couldn't check Hub AI."));
    }
  }, []);

  useEffect(() => {
    if (open && !status) void loadStatus();
  }, [open, status, loadStatus]);

  // Keep the newest message in view.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [thread.turns.length, pending, open, historyOpen]);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (mode !== "floating" || !open) return;
    function onKey(event: globalThis.KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, open]);

  useEffect(() => {
    if (!floating) return;
    function onResize() {
      setGeometry((g) => clampGeometry(g));
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [floating]);

  const persist = useCallback((next: Thread) => {
    if (usedSlack(next.turns)) {
      // Drop any copy saved before this chat touched Slack; the tab keeps it until reload.
      if (!unsavedSlackChats.has(next.id)) {
        unsavedSlackChats.add(next.id);
        void deleteHubAssistConversation(next.id).catch(() => undefined);
      }
      return;
    }
    const messages = storedFrom(next.turns);
    if (!messages.length) return;
    void saveHubAssistConversation(next.id, { module: next.module, messages }).catch(() => {
      /* best effort, like One AI: the chat keeps working if saving fails */
    });
  }, []);

  const updateTurn = useCallback(
    (id: string, change: (turn: Turn) => Turn, save = true) => {
      setThread((prev) => {
        const next = { ...prev, turns: prev.turns.map((t) => (t.id === id ? change(t) : t)) };
        if (save) persist(next);
        return next;
      });
    },
    [persist, setThread],
  );

  const ask = useCallback(
    async (text: string) => {
      const question = text.trim();
      if (!question || pending) return;
      const userTurn: Turn = { id: turnId(), role: "user", content: question, at: new Date().toISOString() };
      const base = threadRef.current;
      const module = base.turns.length ? base.module : pageCtx.module;
      const withUser = { ...base, module, turns: [...base.turns, userTurn] };
      setThread(withUser);
      setInput("");
      setPending(true);
      const focus = focusRef.current;
      focusRef.current = undefined;
      try {
        const reply = await askHubAssist(historyFor(withUser.turns), {
          module: pageCtx.module,
          label: pageCtx.label,
          path: pathname,
          ...(focus ? { focus } : {}),
        });
        const assistant: Turn = {
          id: turnId(),
          role: "assistant",
          content: reply.reply,
          at: new Date().toISOString(),
          toolCalls: reply.toolCalls,
          elapsedMs: reply.elapsedMs,
          proposals: reply.proposals,
          proposalState: Object.fromEntries(reply.proposals.map((p) => [p.id, { phase: "ready" as const }])),
          drafts: reply.proposedSends,
          draftState: Object.fromEntries(reply.proposedSends.map((d) => [d.id, { phase: "ready" as const, text: d.text }])),
        };
        setThread((prev) => {
          const next = { ...prev, turns: [...prev.turns, assistant] };
          persist(next);
          return next;
        });
      } catch (err) {
        if (hubAiErrorCode(err) === "llm_not_configured") void loadStatus();
        setThread((prev) => ({
          ...prev,
          turns: [
            ...prev.turns,
            { id: turnId(), role: "error", content: hubAiErrorMessage(err, "Hub AI couldn't answer."), at: new Date().toISOString() },
          ],
        }));
      } finally {
        setPending(false);
      }
    },
    [loadStatus, pageCtx.label, pageCtx.module, pathname, pending, persist, setThread],
  );

  // Open from anywhere: bottom bar, Meetings rows, Tasks page.
  const askRef = useRef(ask);
  askRef.current = ask;
  useEffect(() => {
    if (mode !== "floating") return;
    function onOpen(event: Event) {
      const detail = (event as CustomEvent<HubAiOpenDetail>).detail || {};
      setOpen(true);
      setHistoryOpen(false);
      if (detail.fresh && threadRef.current.turns.length) {
        setThread({ id: newConversationId(), turns: [], module: "hub" });
      }
      focusRef.current = detail.focus;
      if (detail.prompt && detail.send) void askRef.current(detail.prompt);
      else if (detail.prompt) setInput(detail.prompt);
    }
    window.addEventListener(HUB_AI_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(HUB_AI_OPEN_EVENT, onOpen);
  }, [mode, setThread]);

  function newChat() {
    setThread({ id: newConversationId(), turns: [], module: pageCtx.module });
    setHistoryOpen(false);
    setInput("");
    focusRef.current = undefined;
    window.setTimeout(() => inputRef.current?.focus(), 30);
  }

  async function openHistory() {
    setHistoryOpen(true);
    setHistoryError("");
    try {
      setHistory(await listHubAssistConversations());
    } catch (err) {
      setHistoryError(hubAiErrorMessage(err, "Couldn't load history."));
    }
  }

  async function pickConversation(id: string) {
    try {
      const convo = await loadHubAssistConversation(id);
      setThread({ id: convo.id, module: convo.module, turns: turnsFrom(convo.messages) });
      setHistoryOpen(false);
    } catch (err) {
      setHistoryError(hubAiErrorMessage(err, "Couldn't open that conversation."));
    }
  }

  async function removeConversation(id: string) {
    setHistory((rows) => rows?.filter((row) => row.id !== id) ?? rows);
    try {
      await deleteHubAssistConversation(id);
    } catch {
      void openHistory();
    }
    if (threadRef.current.id === id) setThread({ id: newConversationId(), turns: [], module: pageCtx.module });
  }

  async function confirmProposal(turn: Turn, proposal: HubAssistProposal) {
    const state = turn.proposalState?.[proposal.id] ?? { phase: "ready" };
    updateTurn(turn.id, (t) => ({ ...t, proposalState: { ...t.proposalState, [proposal.id]: { ...state, phase: "saving" } } }), false);
    try {
      const result = await confirmHubAssist(proposal.id, proposal.kind === "create_tasks" ? state.include : undefined);
      announceTasksChanged();
      updateTurn(turn.id, (t) => ({
        ...t,
        proposalState: { ...t.proposalState, [proposal.id]: { ...state, phase: "done", message: result.message } },
      }));
    } catch (err) {
      const expired = hubAiErrorCode(err) === "proposal_expired";
      updateTurn(turn.id, (t) => ({
        ...t,
        proposalState: {
          ...t.proposalState,
          [proposal.id]: { ...state, phase: "error", message: expired ? "This suggestion expired. Ask Hub AI again." : hubAiErrorMessage(err, "Couldn't save that.") },
        },
      }), expired);
    }
  }

  function cancelProposal(turn: Turn, proposal: HubAssistProposal) {
    updateTurn(turn.id, (t) => ({ ...t, proposalState: { ...t.proposalState, [proposal.id]: { phase: "cancelled" } } }));
  }

  async function sendDraft(turn: Turn, draft: HubAiProposedSend) {
    const state = turn.draftState?.[draft.id] ?? { phase: "ready", text: draft.text };
    updateTurn(turn.id, (t) => ({ ...t, draftState: { ...t.draftState, [draft.id]: { ...state, phase: "sending" } } }), false);
    try {
      const result = await sendHubAiDraft({ ...draft, text: state.text });
      updateTurn(turn.id, (t) => ({
        ...t,
        draftState: { ...t.draftState, [draft.id]: { ...state, phase: "sent", permalink: result.permalink } },
      }));
    } catch (err) {
      updateTurn(turn.id, (t) => ({
        ...t,
        draftState: { ...t.draftState, [draft.id]: { ...state, phase: "error", error: hubAiErrorMessage(err, "Slack didn't send it.") } },
      }), false);
    }
  }

  function onListClick(event: ReactMouseEvent<HTMLDivElement>) {
    const target = event.target instanceof Element ? event.target.closest("a[data-hub-route]") : null;
    if (!target) return;
    event.preventDefault();
    const href = target.getAttribute("href") || "/";
    navigate(href);
    if (mode === "floating" && !desktop) setOpen(false);
  }

  function goToTasks() {
    navigate("/tasks");
    if (mode === "floating" && !desktop) setOpen(false);
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    void ask(input);
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void ask(input);
    }
  }

  // Drag by the header, resize from the corner (desktop floating only).
  function startDrag(event: ReactPointerEvent<HTMLElement>) {
    if (!floating || event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest("button")) return;
    event.preventDefault();
    const start = { px: event.clientX, py: event.clientY, ...geometry };
    let last = geometry;
    const move = (e: PointerEvent) => {
      last = clampGeometry({ ...start, x: start.x + e.clientX - start.px, y: start.y + e.clientY - start.py });
      setGeometry(last);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      writeGeometry(last);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  function startResize(event: ReactPointerEvent<HTMLElement>) {
    if (!floating || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const start = { px: event.clientX, py: event.clientY, ...geometry };
    let last = geometry;
    const move = (e: PointerEvent) => {
      last = clampGeometry({ ...start, w: start.w + e.clientX - start.px, h: start.h + e.clientY - start.py });
      setGeometry(last);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      writeGeometry(last);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  if (!open) return null;

  const notConfigured = status ? !status.llm.configured : false;
  const turns = thread.turns;
  const lastIsAnswer = turns.length > 0 && turns[turns.length - 1]!.role !== "user";
  const chips = !pending && (!turns.length || lastIsAnswer) ? pageCtx.suggestions : [];

  const panel = (
    <div
      role={mode === "floating" ? "dialog" : "region"}
      aria-modal={mode === "floating" ? false : undefined}
      aria-label="Hub AI"
      className={cn("hub-ai-panel", mode === "page" ? "is-page" : floating ? "is-floating" : "is-sheet")}
      style={floating ? { left: geometry.x, top: geometry.y, width: geometry.w, height: geometry.h } : undefined}
    >
      <header className={cn("hub-ai-panel-head", floating && "is-draggable")} onPointerDown={startDrag}>
        <div className="hub-ai-panel-title">
          <p className="hub-ai-panel-name">
            <Sparkles size={14} aria-hidden /> Hub AI
          </p>
          <p className="hub-ai-panel-tagline">Meetings, tasks, and the rest of your Hub.</p>
          <p className="hub-ai-panel-where">On {pageCtx.label}</p>
        </div>
        <div className="hub-ai-panel-actions">
          <button type="button" aria-label="History" title="History" onClick={() => (historyOpen ? setHistoryOpen(false) : void openHistory())}>
            <History size={16} />
          </button>
          <button type="button" aria-label="New chat" title="New chat" onClick={newChat}>
            <Plus size={16} />
          </button>
          {mode === "floating" ? (
            <button type="button" aria-label="Close Hub AI" title="Close" onClick={() => setOpen(false)}>
              <X size={16} />
            </button>
          ) : null}
        </div>
      </header>

      <div className="hub-ai-panel-body">
        {historyOpen ? (
          <div className="hub-ai-panel-history">
            <div className="hub-ai-panel-history-head">
              <span>History</span>
              <button type="button" className="hub-ai-link-btn" onClick={() => setHistoryOpen(false)}>
                Back to chat
              </button>
            </div>
            {historyError ? <p className="hub-ai-panel-error">{historyError}</p> : null}
            {!history && !historyError ? (
              <p className="hub-ai-panel-pending">
                <LoaderCircle size={14} className="spinning" aria-hidden /> Loading…
              </p>
            ) : null}
            {history && !history.length ? <p className="muted hub-ai-panel-empty-history">No saved conversations yet.</p> : null}
            <ul>
              {(history || []).map((row) => (
                <li key={row.id} className={cn(row.id === thread.id && "active")}>
                  <button type="button" className="hub-ai-panel-history-item" onClick={() => void pickConversation(row.id)}>
                    <span className="hub-ai-panel-history-title">{row.title}</span>
                    <span className="hub-ai-panel-history-preview">{row.preview}</span>
                    <span className="hub-ai-panel-history-date">{timeLabel(row.updatedAt)}</span>
                  </button>
                  <button
                    type="button"
                    className="hub-ai-panel-history-delete"
                    aria-label={`Delete ${row.title}`}
                    title="Delete"
                    onClick={() => void removeConversation(row.id)}
                  >
                    <Trash2 size={13} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div ref={listRef} className="hub-ai-panel-list" aria-live="polite" onClick={onListClick}>
          {notConfigured ? (
            <div className="hub-ai-panel-callout" role="status">
              <TriangleAlert size={15} aria-hidden />
              <p>Hub AI isn't switched on yet. The Hub API needs its Anthropic API key.</p>
            </div>
          ) : null}
          {statusError ? <p className="hub-ai-panel-error">{statusError}</p> : null}
          {!turns.length ? (
            <div className="hub-ai-panel-welcome">
              <p className="hub-ai-panel-welcome-title">Hi, I'm Hub AI</p>
              <p>
                You're on <b>{pageCtx.label}</b>. I can prep you for meetings, turn them into follow-up tasks, plan your
                day, and look across mail, calendar{status?.slack.connected ? ", and Slack" : ""}. I'll always ask before I
                change anything.
              </p>
            </div>
          ) : null}
          {turns.map((turn) => {
            if (turn.role === "user") {
              return (
                <div key={turn.id} className="hub-ai-panel-msg is-user">
                  <p>{turn.content}</p>
                </div>
              );
            }
            if (turn.role === "error") {
              return (
                <p key={turn.id} role="alert" className="hub-ai-panel-error">
                  {turn.content}
                </p>
              );
            }
            return (
              <div key={turn.id} className="hub-ai-panel-turn">
                <div className="hub-ai-panel-msg is-assistant">
                  {turn.toolCalls?.length ? <ToolTrail calls={turn.toolCalls} elapsedMs={turn.elapsedMs} /> : null}
                  <div className="hub-ai-panel-text">{renderAiText(turn.content)}</div>
                </div>
                {(turn.proposals || []).map((proposal) => (
                  <ProposalCard
                    key={proposal.id}
                    proposal={proposal}
                    state={turn.proposalState?.[proposal.id] ?? { phase: "ready" }}
                    onInclude={(include) =>
                      updateTurn(turn.id, (t) => ({
                        ...t,
                        proposalState: { ...t.proposalState, [proposal.id]: { ...(t.proposalState?.[proposal.id] ?? { phase: "ready" }), include } },
                      }), false)
                    }
                    onConfirm={() => void confirmProposal(turn, proposal)}
                    onCancel={() => cancelProposal(turn, proposal)}
                    onOpenTasks={goToTasks}
                  />
                ))}
                {(turn.drafts || []).map((draft) => (
                  <SlackDraftCard
                    key={draft.id}
                    draft={draft}
                    state={turn.draftState?.[draft.id] ?? { phase: "ready", text: draft.text }}
                    onText={(text) =>
                      updateTurn(turn.id, (t) => ({
                        ...t,
                        draftState: { ...t.draftState, [draft.id]: { ...(t.draftState?.[draft.id] ?? { phase: "ready" }), text } },
                      }), false)
                    }
                    onSend={() => void sendDraft(turn, draft)}
                    onCancel={() =>
                      updateTurn(turn.id, (t) => ({
                        ...t,
                        draftState: { ...t.draftState, [draft.id]: { ...(t.draftState?.[draft.id] ?? { text: draft.text }), phase: "cancelled" } },
                      }))
                    }
                  />
                ))}
              </div>
            );
          })}
          {pending ? (
            <p className="hub-ai-panel-pending">
              <LoaderCircle size={14} className="spinning" aria-hidden /> Thinking…
            </p>
          ) : null}
        </div>

        {chips.length ? (
          <div className="hub-ai-panel-chips">
            {chips.map((chip) => (
              <button key={chip} type="button" disabled={notConfigured} onClick={() => void ask(chip)}>
                {chip}
              </button>
            ))}
          </div>
        ) : null}

        <form className="hub-ai-panel-composer" onSubmit={onSubmit}>
          <textarea
            ref={inputRef}
            rows={1}
            value={input}
            placeholder="Ask Hub AI…"
            aria-label="Ask Hub AI"
            maxLength={4000}
            disabled={notConfigured}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={onKeyDown}
          />
          <button type="submit" aria-label="Send" disabled={pending || notConfigured || !input.trim()}>
            <ArrowUp size={16} />
          </button>
        </form>
        <p className="hub-ai-panel-foot">
          {usedSlack(turns) ? (
            <span>This chat used Slack, so it isn't saved to History · </span>
          ) : null}
          Build {HUB_VERSION}
          {floating ? <span> · Drag header · resize corner</span> : null}
        </p>
        {floating ? <span className="hub-ai-panel-resize" aria-hidden onPointerDown={startResize} /> : null}
      </div>
    </div>
  );

  return mode === "floating" ? createPortal(panel, document.body) : panel;
}
